import type { FC, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { cn } from 'src/utils/cn';

export const metaLabelColors = {
  success: cn('text-foreground-meta-label-success'),
  error: cn('text-foreground-meta-label-error'),
  pending: cn('text-foreground-meta-label-pending'),
  inactive: cn('text-foreground-meta-label-inactive'),
  entry: cn('text-foreground-meta-label-entry'),
  exit: cn('text-foreground-meta-label-exit'),
  bundle: cn('text-foreground-meta-label-bundle'),
  primary: cn('text-foreground-meta-label-primary'),
  secondary: cn('text-foreground-meta-label-secondary'),
  info: cn('text-foreground-meta-label-info'),
  own: cn('text-foreground-meta-label-own'),
  ready: cn('text-foreground-meta-label-ready'),
} as const;

export type MetaLabelColor = keyof typeof metaLabelColors;

export type MetaLabelProps = {
  color: MetaLabelColor;
  children: ReactNode;
  'data-testid'?: string;
  className?: string;
};

export const MetaLabel: FC<MetaLabelProps> = ({
  color,
  children,
  ['data-testid']: testId = 'meta-label',
  className,
}) => {
  const contextMode = useMetaLabelMode();
  return (
    <span
      className={cn(
        'font-label-semibold',
        contextMode === 'page' ? 'text-label-md' : 'text-label-sm',
        metaLabelColors[color],
        className
      )}
      data-testid={testId}
    >
      {children}
    </span>
  );
};

type MetaLabelsProps = {
  children: ReactNode;
  'data-testid'?: string;
};

export const MetaLabels: FC<MetaLabelsProps> = ({
  children,
  ['data-testid']: testId = 'meta-labels',
}) => {
  const contextMode = useMetaLabelMode();
  return (
    <div
      className={cn(
        'flex flex-row flex-wrap',
        contextMode === 'page' ? 'gap-x-lg gap-y-0' : 'gap-x-md gap-y-0'
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

// Provider
type MetaLabelMode = 'page' | 'standalone';

const MetaLabelContext = createContext<MetaLabelMode>('standalone');

export function useMetaLabelMode() {
  return useContext(MetaLabelContext);
}

export const MetaLabelProvider = MetaLabelContext.Provider;
